/**
 * SortableNoteCard
 *
 * Wraps NoteCard in a @dnd-kit/sortable draggable item.
 * Used exclusively on HomePage for drag-to-reorder of pinned notes.
 *
 * The drag handle is the entire card; a small "grip" icon overlay on hover
 * communicates draggability without cluttering the card UI.
 */
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import NoteCard from './NoteCard'
import { Note } from '../api/notes'

interface SortableNoteCardProps {
  note: Note
  onToggleStar?: (noteId: string) => void
  onToggleHome?: (noteId: string) => void
}

export default function SortableNoteCard({ note, onToggleStar, onToggleHome }: SortableNoteCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: note.id })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    // Elevate the card while dragging
    zIndex: isDragging ? 50 : undefined,
    position: 'relative',
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="group/sortable relative"
      data-testid="sortable-card"
    >
      {/* Drag handle indicator — visual affordance only, no extra listeners needed */}
      <div
        data-testid="drag-handle"
        className={`
          absolute top-2 left-2 z-10 p-1 rounded cursor-grab active:cursor-grabbing
          bg-white/80 border border-gray-200 shadow-sm pointer-events-none
          opacity-0 group-hover/sortable:opacity-100 transition-opacity
          ${isDragging ? 'opacity-100' : ''}
        `}
        title="Drag to reorder"
      >
        <svg className="w-3.5 h-3.5 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
          <circle cx="9" cy="6" r="1.5" />
          <circle cx="15" cy="6" r="1.5" />
          <circle cx="9" cy="12" r="1.5" />
          <circle cx="15" cy="12" r="1.5" />
          <circle cx="9" cy="18" r="1.5" />
          <circle cx="15" cy="18" r="1.5" />
        </svg>
      </div>

      <NoteCard
        note={note}
        onToggleStar={onToggleStar}
        onToggleHome={onToggleHome}
      />
    </div>
  )
}
